import { useMemo } from "react";

import { addDays, addMinutes, differenceInMinutes, set } from "date-fns";
import { memoize } from "lodash";

import type { SelectorOption } from "~/ui/selectors/SelectorUtils";

export interface TimeValue {
	hour: number;
	minute: number;
}

export interface TimeOption extends SelectorOption {
	readonly timeValue: TimeValue;
	readonly singleValueLabel: string;
}

export const isEqualTimeValue = (a: TimeValue, b: TimeValue) => {
	return a.hour === b.hour && a.minute === b.minute;
};

// /**
//  * A helper for rounding the time to the nearest 15 minutes, without going back in time.
//  * @param time The time to round.
//  * @returns A new time value in the future that is rounded to the nearest 15 minutes,
//  *  if the time didn't already fall on a 15 minute demarcation.
//  */
// const roundForwardToNearestDemarcation = (time: TimeValue): TimeValue => {
// 	if (time.minute > 45) return { hour: time.hour < 24 ? time.hour + 1 : 0, minute: 0 };
// 	else if (time.minute > 30) return { hour: time.hour, minute: 45 };
// 	else if (time.minute > 15) return { hour: time.hour, minute: 30 };
// 	else if (time.minute > 0) return { hour: time.hour, minute: 15 };
// 	else return time;
// };

/**
 * @param time The time to convert to a Date.
 * @param baseDate A date object used to fill in the year, month, and day of the returned Date. Defaults to `new Date()`.
 * @returns A Date object with the same hour and minute as the provided time.
 */
export const timeValueToDate = (time: TimeValue, baseDate: Date = new Date()) => {
	return set(baseDate, { hours: time.hour, minutes: time.minute, seconds: 0, milliseconds: 0 });
};
export const dateToTimeValue = (date: Date): TimeValue => ({ hour: date.getHours(), minute: date.getMinutes() });

/**
 *
 * @param time The time to turn into an valid Selector option
 * @param timeToCompare When provided, displays a relative time label (i.e. 4 hours) alongside the option
 * @returns A SelectorOption to be used in a Selector
 */
export const makeTimeOption = (time: TimeValue, timeToCompare?: TimeValue): TimeOption => {
	const { hour, minute } = time;

	// If `time` is before `timeToCompare`, assume it means that `time` is in the next day
	const baseDate =
		(timeToCompare && hour < timeToCompare.hour) ||
		(timeToCompare && hour === timeToCompare.hour && minute < timeToCompare.minute)
			? addDays(new Date(), 1)
			: new Date();
	const timeAsDate = timeValueToDate({ hour, minute }, baseDate);

	let timeLabel = new Intl.DateTimeFormat("en-US", { minute: "2-digit", hour: "numeric" }).format(timeAsDate);

	if (timeToCompare) {
		let timeToCompareAsDate = timeValueToDate(timeToCompare);

		const duration = differenceInMinutes(timeAsDate, timeToCompareAsDate);
		const durationInHours = duration / 60;

		if (duration < 60) {
			timeLabel += ` (${`${duration} min${duration !== 1 ? "s" : ""}`})`;
		} else {
			timeLabel += ` (${`${durationInHours} hr${durationInHours !== 1 ? "s" : ""}`})`;
		}
	}

	return {
		timeValue: { hour, minute },
		singleValueLabel: timeLabel,
		label: timeLabel,
		value: timeLabel, // use timeValue instead
	};
};

/**
 * A function for guessing a time from a string. This is used to allow users to type in a time in a text input.
 * @param inputValue The string to parse.
 */
export const guessTimeFromString = memoize((inputValue: string): TimeOption | null => {
	const incompleteTime = inputValue.trim();

	const reg24hr = /^(\d{1,2})(:?)(\d{0,2})$/;

	let matches = reg24hr.exec(incompleteTime);
	if (matches !== null) {
		const hourStr = matches[1];
		let hour = parseInt(hourStr);
		const colon = matches[2];
		const minuteStr = matches[3];
		let minute = minuteStr ? parseInt(minuteStr) : 0;

		// If the minute is a single digit, the user is likely referring to the 10s place
		// Ex: 2:3 -> 2:30 or 12:3 -> 12:30
		if (colon && minuteStr.length === 1) minute *= 10;

		// A triple digit number "hmm" is interpreted "h:mm" and not "hh:m"
		if (!colon && matches[0].length === 3) {
			hour = parseInt(matches[0][0]);
			minute = parseInt(matches[0].slice(1));
		}

		if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return null;

		return makeTimeOption({ hour, minute });
	}

	const reg12hr = /^(\d{1,2})(?::?(\d{2}))?\s*([a|p]m?)?$/i;

	matches = reg12hr.exec(incompleteTime);
	if (matches !== null) {
		let minute = matches[2] ? parseInt(matches[2]) : 0;
		let hour = parseInt(matches[1]);

		if (hour < 1 || hour > 12 || minute < 0 || minute > 59) return null;

		const meridiem = matches[3];
		switch (meridiem) {
			case "p":
			case "pm": {
				if (hour !== 12) hour += 12;
				break;
			}
			case "a":
			case "am": {
				if (hour === 12) hour = 0;
				break;
			}
			default: {
				return null;
			}
		}

		return makeTimeOption({ hour, minute });
	}

	return null;
});

// export function useTimeState() {
// 	const [time, _setTime] = useState<TimeValue>(() => {
// 		const now = new Date();
// 		return roundForwardToNearestDemarcation({ hour: now.getHours(), minute: now.getMinutes() });
// 	});

// 	const setTime = useCallback((time: TimeValue) => {
// 		if (!(time.hour < 24 && time.minute < 60 && time.hour >= 0 && time.minute >= 0)) {
// 			throw new Error(`Invalid time: ${time.hour}:${time.minute}`);
// 		}

// 		setTime(time);
// 	}, []);

// 	return {
// 		time,
// 		setTime,
// 	};
// }

/**
 * @param timeToCompare When provided, displays a relative time label (i.e. 4 hours) alongside each option.
 * 	This method assumes that timeToCompare at most 24 hours behind of the options to be displayed.
 * 	Please omit if this is not the case.
 * @returns An array of SelectorOptions that can be used in a Selector.
 */
export const useTimeOptions = (timeToCompare?: TimeValue) => {
	const options = useMemo(() => {
		const clockDemarcations = [0, 15, 30, 45];
		const times: Array<TimeOption> = [];

		if (!timeToCompare) {
			for (let hour = 0; hour < 24; hour++) {
				clockDemarcations.forEach(mark => times.push(makeTimeOption({ hour, minute: mark })));
			}
		} else {
			const timeToCompareAsDate = timeValueToDate(timeToCompare);

			// 24 hours * (60 minutes / 15 min demarcations) = 96 times
			for (let count = 1; count < 24 * 4; count++) {
				const runningOptionAsDate = addMinutes(timeToCompareAsDate, 15 * count);
				times.push(makeTimeOption(dateToTimeValue(runningOptionAsDate), timeToCompare));
			}
		}
		return times;
	}, [timeToCompare]);
	return options;
};
